import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { legalContent } from '../legalContent';

/**
 * LegalPage — renders Privacy / Terms / Impressum in NL / DE.
 *
 * Single-column layout that fills the 1440px global rail. No
 * left/right split — every paragraph and list flows full width
 * inside the shared container padding.
 *
 * Hyphenation is `manual` and relies on Soft-Hyphens (\u00AD) baked
 * into the title strings in `legalContent.js` — `hyphens: auto` is
 * unreliable for German compound nouns ("Datenschutzerklärung" must
 * break at "Datenschutz-erklärung", never at "Datenschutzerklä-rung").
 */

// Last revision date — shown as a small "Stand" / "Versie" badge near
// the top of every legal page. Keep this in sync with legalContent.js.
const LAST_UPDATED = { nl: '23 mei 2026', de: '23. Mai 2026' };

export default function LegalPage({ kind, lang, onBack }) {
  const doc = legalContent[kind]?.[lang] || legalContent[kind].nl;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [kind, lang]);

  const backLabel = lang === 'de' ? 'Zurück zur Startseite' : 'Terug naar de startpagina';
  const stampLabel = lang === 'de' ? 'Stand' : 'Versie';

  return (
    <article data-testid={`legal-page-${kind}`} className="relative bg-[#FAF8F2] text-[#1F1D1A] min-h-screen pt-28 sm:pt-32 pb-24">
      <div className="max-w-[1440px] mx-auto px-5 md:px-8 lg:px-10">
        {/* Back link */}
        <button
          onClick={onBack}
          data-testid="legal-back"
          className="inline-flex items-center gap-2 text-[13px] text-[#5C5852] hover:text-[#1F1D1A] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </button>

        {/* "Stand · 23. Mai 2026" badge */}
        <div
          data-testid="legal-updated-badge"
          className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 rounded-full bg-[#F26B1A]/10 border border-[#F26B1A]/25 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#F26B1A]"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#F26B1A]" />
          {stampLabel} · {LAST_UPDATED[lang]}
        </div>

        <h1
          lang={lang}
          className="font-bowlby text-[#F26B1A] uppercase leading-[0.95] mb-10"
          style={{ fontSize: 'clamp(28px, 6.5vw, 96px)', wordBreak: 'normal', hyphens: 'manual', WebkitHyphens: 'manual' }}
        >
          {doc.title}
        </h1>

        {/* Intro — single column, full rail width */}
        <p data-testid="legal-intro" className="text-[#5C5852] text-[15.5px] md:text-[17px] leading-relaxed mb-16 md:mb-20">
          {doc.intro}
        </p>

        {/* Sections — single column, full rail width */}
        <div className="space-y-12 md:space-y-16">
          {doc.sections.map((sec, i) => (
            <section key={sec.h || `sec-${i}`} className="pt-8 border-t border-[#E5E0D2]">
              <h2 className="font-display text-[#1F1D1A] text-[22px] sm:text-[26px] md:text-[30px] leading-tight mb-5">
                {sec.h}
              </h2>
              {sec.p?.map((para, j) => (
                <p key={`${i}-p-${j}`} className="text-[#1F1D1A]/80 text-[15px] md:text-[16.5px] leading-relaxed mb-3 last:mb-0">
                  {para}
                </p>
              ))}
              {sec.list && (
                <ul className="list-disc pl-5 mt-3 space-y-2 text-[#1F1D1A]/80 text-[15px] md:text-[16.5px] leading-relaxed">
                  {sec.list.map((li, k) => (
                    <li key={`${i}-li-${k}`}>{li}</li>
                  ))}
                </ul>
              )}
              {sec.after && (
                <p className="text-[#1F1D1A]/80 text-[15px] md:text-[16.5px] leading-relaxed mt-4">{sec.after}</p>
              )}
            </section>
          ))}
        </div>

        {/* Footer line */}
        <div className="mt-20 pt-8 border-t border-[#E5E0D2] text-[12.5px] text-[#5C5852]">
          fliro.tech B.V. · Veldoven 6 · 6826 TT Arnhem · KvK 42004418
        </div>
      </div>
    </article>
  );
}
